import {
  ReactElement,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { AccordionPanelProps } from '../jdsaccordion/logic';
import { JDSAccordionListProps } from './generated';

export interface AccordionProps
  extends Omit<JDSAccordionListProps, 'children'> {
  /**
   * If false/undefined, only allows one accordionPanel to be expanded by collapsing others on each click.
   */
  allowMultiple?: boolean;
  /**
   * List of Panels that should be expanded. This is default, but also when changed again, should reset all panels to provided states.
   */
  expanded?: number[];
  /**
   * Trigger whenever user clicks to expand/collapse accordionPanels as well as when expanded prop is changed
   * @param expandedPanels
   */
  onChange?: (expandedPanels: number[]) => void;
  /**
   * The icon to be displayed. The plus icon changes to a minus icon when the corresponding panel is expanded
   */
  iconType?: 'chevron' | 'plus';
  /**
   * Must be children of type `<AccordionPanel>`
   */
  children?:
    | ReactElement<AccordionPanelProps>
    | ReactElement<AccordionPanelProps>[];
}

const getAllowedPanels = (panels?: number[], allowMultiple?: boolean) => {
  if (!panels) return [];
  if (allowMultiple) {
    return panels;
  }
  return [panels[0]];
};

export const withLogic =
  (Component: React.ComponentType<AccordionProps>): React.FC<AccordionProps> =>
  (props: AccordionProps) => {
    const { allowMultiple, children, onChange, iconType } = props;

    const [selected, setSelected] = useState<number[]>(
      getAllowedPanels(props.expanded, allowMultiple)
    );

    useEffect(() => {
      setSelected(getAllowedPanels(props.expanded, allowMultiple));
    }, [props.expanded, allowMultiple]);

    const childrenArray = useMemo(() => {
      if (!children) return [];
      if (Array.isArray(children)) return children;
      if (
        (children.type as unknown as symbol) === Symbol.for('react.fragment')
      ) {
        const validChild = children.props.children;
        return Array.isArray(validChild) ? validChild : [validChild];
      }
      return [children];
    }, [children]);

    const handleSelected = useCallback(
      (i: number) => {
        let newSelected = [...selected];
        if (selected.includes(i)) {
          const idx = newSelected.findIndex((s) => s === i);
          if (allowMultiple) {
            if (idx >= 0) newSelected.splice(idx, 1);
          } else {
            newSelected = [];
          }
        } else {
          if (!allowMultiple) {
            newSelected = [i];
          } else {
            newSelected.push(i);
          }
        }
        setSelected(newSelected);
        onChange?.(newSelected);
      },
      [selected, allowMultiple, onChange]
    );

    const expandedChildren = useMemo(
      () =>
        childrenArray?.map((c, i: number) => {
          return cloneElement<AccordionPanelProps>(c, {
            ...c.props,
            iconType: iconType || c.props.iconType,
            onClick: (e) => {
              handleSelected(i);
              c.props.onClick?.(e);
            },
            expandedPanel: c?.props?.expandedPanel || selected.includes(i),
            disabled: Boolean(c.props?.disabled)
          });
        }),
      [childrenArray, handleSelected, iconType, selected]
    );
    return (
      <Component
        {...{
          ...props,
          _hasItems: Boolean(props.items?.length),
          items: props?.items?.map((item, i) => {
            return {
              ...item,
              iconType,
              onClick: (e) => {
                handleSelected(i);
                item.onClick?.(e);
              },
              expandedPanel: item?.expandedPanel || selected.includes(i),
              disabled: Boolean(item?.disabled)
            };
          })
        }}
      >
        {expandedChildren}
      </Component>
    );
  };
